import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { DateTime } from 'luxon'
import { detectPixType, normalizePixType, pixKeyToRaw, validatePixKey, PixKeyType } from './pixKey'

export const BATCH_TIMEZONE = 'America/Sao_Paulo'

export type BatchItemDraft = {
  externalId: string
  name?: string
  document?: string
  pixKey: string
  pixKeyType?: PixKeyType
  description?: string
  amount: number
  /** YYYY-MM-DD. Ausente = paga hoje. */
  scheduledFor?: string
  errors: string[]
}

const ALIASES: Record<
  'name' | 'document' | 'pixKey' | 'amount' | 'externalId' | 'pixKeyType' | 'description' | 'scheduledFor',
  string[]
> = {
  name: ['nome', 'name'],
  document: ['documento', 'document', 'cpf', 'cnpj', 'cpf_cnpj'],
  pixKey: ['chave_pix', 'chavepix', 'chave', 'pix', 'pixkey'],
  amount: ['valor', 'amount', 'value'],
  externalId: ['identificador', 'id_unico', 'idunico', 'id', 'external_id', 'externalid'],
  pixKeyType: ['tipo_chave', 'tipo', 'tipochave', 'tipo_pix', 'tipopix', 'key_type', 'keytype', 'tipo_da_chave'],
  description: ['descricao', 'description', 'desc', 'mensagem', 'observacao'],
  // Sem o alias solto `data`: uma planilha com coluna "Data" de outra coisa
  // (competência, vencimento) e valor futuro agendaria TODOS os pagamentos em
  // silêncio — nada obriga o usuário a conferir a pré-visualização, e é dinheiro.
  scheduledFor: ['data_pagamento', 'data_do_pagamento', 'payment_date', 'agendamento'],
}

const normalize = (h: string) =>
  String(h)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '_')

const pick = (record: Record<string, any>, keys: string[]) => {
  for (const k of Object.keys(record)) {
    if (keys.includes(normalize(k))) return record[k]
  }
  return undefined
}

/**
 * Lê a célula de data de pagamento. Vazia = paga hoje.
 *
 * Devolve `YYYY-MM-DD` porque é isso que a API aceita: a hora (08:00) e o fuso
 * são compostos no servidor. Serial numérico do Excel não é aceito de
 * propósito — o `parseFile` lê a planilha com `cellDates`, então data de
 * verdade chega como `Date`; número solto aqui é coluna errada, e errar alto é
 * melhor que pagar no dia errado.
 */
export function parsePaymentDate(
  input: unknown,
  now: DateTime = DateTime.now().setZone(BATCH_TIMEZONE),
): { value?: string; error?: string } {
  if (input === undefined || input === null) return {}

  let parsed: DateTime | null = null
  if (input instanceof Date) {
    parsed = DateTime.fromObject(
      { year: input.getFullYear(), month: input.getMonth() + 1, day: input.getDate() },
      { zone: BATCH_TIMEZONE },
    )
  } else {
    const raw = String(input).trim()
    if (!raw) return {}
    parsed =
      [
        DateTime.fromFormat(raw, 'dd/MM/yyyy', { zone: BATCH_TIMEZONE }),
        DateTime.fromFormat(raw, 'yyyy-MM-dd', { zone: BATCH_TIMEZONE }),
      ].find((candidate) => candidate.isValid) ?? null
  }

  if (!parsed || !parsed.isValid) return { error: 'data de pagamento inválida' }

  const today = now.setZone(BATCH_TIMEZONE).startOf('day')
  const day = parsed.startOf('day')
  if (day < today) return { error: 'data de pagamento no passado' }
  // `>=` e não `>`: o teto do servidor (`resolveScheduledFor`) recusa o dia
  // exatamente um ano à frente, porque lá a comparação final é por INSTANTE
  // (`now + 1 ano` contra as 08:00 da data). Com `>` aqui, a pré-visualização
  // aprovaria a linha e o `/validate` a reprovaria — o erro tem que aparecer
  // onde o usuário ainda pode corrigir a planilha.
  if (day >= today.plus({ years: 1 })) return { error: 'data de pagamento acima de 1 ano' }

  return { value: day.toFormat('yyyy-MM-dd') }
}

export function mapRows(records: Record<string, any>[]): BatchItemDraft[] {
  const seen = new Set<string>()
  return records.map((rec) => {
    const externalId = String(pick(rec, ALIASES.externalId) ?? '').trim()
    const pixKeyRaw = String(pick(rec, ALIASES.pixKey) ?? '').trim()
    const name = String(pick(rec, ALIASES.name) ?? '').trim() || undefined
    const document = String(pick(rec, ALIASES.document) ?? '').trim() || undefined
    const typeCell = String(pick(rec, ALIASES.pixKeyType) ?? '').trim()
    const description = String(pick(rec, ALIASES.description) ?? '').trim() || undefined
    const amount = parseBrCurrency(pick(rec, ALIASES.amount))
    const schedule = parsePaymentDate(pick(rec, ALIASES.scheduledFor))
    const errors: string[] = []
    if (schedule.error) errors.push(schedule.error)
    if (!externalId) errors.push('id_unico é obrigatório')
    else if (seen.has(externalId)) errors.push('id_unico duplicado')
    seen.add(externalId)

    let pixKeyType: PixKeyType | '' = ''
    if (typeCell) {
      const normd = normalizePixType(typeCell)
      if (!normd) errors.push('tipo de chave inválido')
      else pixKeyType = normd
    } else if (pixKeyRaw) {
      pixKeyType = detectPixType(pixKeyRaw)
      if (!pixKeyType) errors.push('tipo de chave não identificado')
    }

    let pixKey = pixKeyRaw
    if (!pixKeyRaw) {
      errors.push('chave Pix é obrigatória')
    } else if (pixKeyType) {
      const verr = validatePixKey(pixKeyRaw, pixKeyType)
      if (verr) errors.push(verr)
      else pixKey = pixKeyToRaw(pixKeyRaw, pixKeyType)
    }

    if (amount === null || !(amount > 0)) errors.push('valor inválido')
    return {
      externalId,
      name,
      document,
      pixKey,
      pixKeyType: pixKeyType || undefined,
      description,
      amount: amount ?? 0,
      scheduledFor: schedule.value,
      errors,
    }
  })
}

// Parser de CSV para o layout do modelo: separador ';', cabeçalho na 1ª linha,
// com suporte a aspas duplas básicas. Evita o papaparse, cujo campo "browser"
// aponta para o papaparse.min.js (linha única) e estoura a pilha no build do CRA.
function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (c === ';' && !inQuotes) {
      out.push(cur)
      cur = ''
    } else {
      cur += c
    }
  }
  out.push(cur)
  return out
}

export function parseCsv(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return []
  const headers = splitCsvLine(lines[0]).map((h) => h.trim())
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line)
    const rec: Record<string, string> = {}
    headers.forEach((h, i) => {
      rec[h] = (cells[i] ?? '').trim()
    })
    return rec
  })
}

export async function parseFile(file: File): Promise<BatchItemDraft[]> {
  const isExcel = /\.(xlsx|xls)$/i.test(file.name)
  let records: Record<string, any>[]
  if (isExcel) {
    const buffer = await file.arrayBuffer()
    const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
    const sheet = wb.Sheets[wb.SheetNames[0]]
    records = XLSX.utils.sheet_to_json(sheet, { defval: '' })
  } else {
    const text = await file.text()
    records = parseCsv(text)
  }
  return mapRows(records)
}

export function downloadTemplate() {
  const csv =
    'nome;documento;chave_pix;valor;identificador;tipo_chave;descricao;data_pagamento\n' +
    'Fulano de Tal;12345678900;fulano@email.com;1.234,56;PG-0001;email;Pagamento de comissão;\n' +
    'Ciclano de Tal;98765432100;(11) 99999-9999;500,00;PG-0002;celular;Aluguel;25/12/2026\n'
  saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'modelo-saque-lote.csv')
}

export function parseBrCurrency(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null
  if (typeof input === 'number') return Number.isFinite(input) ? input : null
  let s = String(input)
    .trim()
    .replace(/r\$/gi, '')
    .replace(/\s/g, '')
  if (!s) return null
  const hasComma = s.includes(',')
  const hasDot = s.includes('.')
  if (hasComma && hasDot) {
    // BR: ponto é separador de milhar, vírgula é decimal
    s = s.replace(/\./g, '').replace(',', '.')
  } else if (hasComma) {
    s = s.replace(',', '.')
  }
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}
